/**
 * Classements multi-segments du Live School (National / Niveau / Région /
 * Département / Pays / Établissement / Classe). Lit le cache `live-leaderboard`
 * (≤60 s) ; pour les segments géo/établissement/classe, on résout les clés de
 * l'élève (profil, établissement via classe, classes actives). Affiche le Top
 * 100 + « ta position #N/Total » (live, hors Top 100).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  REGION_LABELS,
  COUNTRY_LABELS,
  type RegionCode,
  type CountryCode,
} from '@jeraisonne/db/live';
import { cn } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { MyPositionCard, RankingList, type RankingRow } from '@/features/live-ui/ranking-list';

// Colonnes géo (0155) + RPC resolve_student_school postérieures aux types stale
// (cf. CLAUDE.md) → client générique, comme @/lib/parent-link.
const db = supabase as unknown as SupabaseClient;

export interface LeaderboardRow {
  rank: number;
  student_id: string;
  display_name: string;
  avatar_url: string | null;
  total_score: number;
  correct_count: number;
}
interface LeaderboardResponse {
  top100: LeaderboardRow[];
  me: { rank: number | null; total: number } | null;
}

interface ScopeTab {
  key: string; // scope
  label: string;
  scopeKey: string;
}

export function LeaderboardTabs({
  challengeId,
  niveau,
  userId,
  topLimit,
  unit = 'pts',
  paper = false,
}: {
  challengeId: string;
  niveau: string;
  userId: string;
  /** Limite d'affichage du haut de classement (défaut : le Top 100 du cache). */
  topLimit?: number;
  /** Unité du score (« HORA » pour un live de programme). */
  unit?: string;
  /** Sur l'écran sombre d'un live : onglets de verre, liste en feuille claire. */
  paper?: boolean;
}) {
  // Contexte de l'élève pour les segments à clé.
  const ctxQuery = useQuery({
    queryKey: ['live-scope-context', userId],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const [{ data: sp }, { data: school }, { data: members }] = await Promise.all([
        db
          .from('student_profiles')
          .select('country, region, departement')
          .eq('id', userId)
          .maybeSingle(),
        db.rpc('resolve_student_school', { p_student: userId }),
        db
          .from('class_members')
          .select('class_id, classes(name)')
          .eq('student_id', userId)
          .eq('status', 'active'),
      ]);
      const profile =
        (sp as {
          country: string | null;
          region: RegionCode | null;
          departement: string | null;
        } | null) ?? null;
      const classRows =
        (members as Array<{ class_id: string; classes: { name: string } | null }> | null) ?? [];
      return {
        country: profile?.country ?? null,
        region: profile?.region ?? null,
        departement: profile?.departement ?? null,
        schoolId: (school as string | null) ?? null,
        classes: classRows.map((m) => ({ id: m.class_id, name: m.classes?.name ?? 'Ma classe' })),
      };
    },
  });

  const tabs = useMemo<ScopeTab[]>(() => {
    const ctx = ctxQuery.data;
    const list: ScopeTab[] = [
      { key: 'national', label: 'National', scopeKey: '' },
      { key: 'niveau', label: `Niveau ${niveau}`, scopeKey: niveau },
    ];
    if (!ctx) return list;
    if (ctx.region)
      list.push({ key: 'region', label: REGION_LABELS[ctx.region], scopeKey: ctx.region });
    if (ctx.departement)
      list.push({
        key: 'departement',
        label: `Dépt ${ctx.departement}`,
        scopeKey: ctx.departement,
      });
    if (ctx.country) {
      const label = COUNTRY_LABELS[ctx.country as CountryCode] ?? ctx.country;
      list.push({ key: 'country', label, scopeKey: ctx.country });
    }
    if (ctx.schoolId)
      list.push({ key: 'school', label: 'Mon établissement', scopeKey: ctx.schoolId });
    for (const c of ctx.classes) list.push({ key: 'class', label: c.name, scopeKey: c.id });
    return list;
  }, [ctxQuery.data, niveau]);

  const [active, setActive] = useState(0);
  const tab = tabs[active] ?? tabs[0]!;

  const boardQuery = useQuery({
    queryKey: ['live-leaderboard', challengeId, tab.key, tab.scopeKey],
    enabled: !!challengeId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<LeaderboardResponse>(
        'live-leaderboard',
        {
          body: { challenge: challengeId, scope: tab.key, key: tab.scopeKey },
        },
      );
      if (error) throw error;
      return data as LeaderboardResponse;
    },
  });

  const all = boardQuery.data?.top100 ?? [];
  const rows = topLimit ? all.slice(0, topLimit) : all;

  const me = boardQuery.data?.me ?? null;

  return (
    <div className="w-full">
      {/* Onglets — défilables horizontalement sur mobile. */}
      <div className="-mx-1 mb-4 flex gap-2 overflow-x-auto px-1 pb-1" role="tablist">
        {tabs.map((t, i) => (
          <button
            key={`${t.key}-${t.scopeKey}`}
            type="button"
            role="tab"
            aria-selected={i === active}
            onClick={() => setActive(i)}
            className={cn(
              'min-h-11 shrink-0 rounded-full px-4 text-sm font-semibold transition active:scale-[0.98]',
              i === active
                ? 'bg-linear-to-b from-[#2f86ff] to-[#0566f2] text-white'
                : paper
                  ? 'bg-[rgba(255,255,255,0.08)] text-white ring-1 ring-white/15'
                  : 'bg-ink-100 text-ink-600',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {boardQuery.isLoading ? (
        <p className="text-ink-600 py-8 text-center text-sm">Chargement du classement…</p>
      ) : rows.length === 0 ? (
        <p className="text-ink-600 py-8 text-center text-sm">Classement bientôt disponible.</p>
      ) : (
        <RankingList rows={leaderboardRankingRows(rows, userId)} unit={unit} paper={paper} />
      )}

      {me?.rank != null && me.total > 0 && (
        <MyPositionCard
          rank={me.rank}
          total={me.total}
          className={cn('mt-3', paper && 'theme-paper')}
        />
      )}
    </div>
  );
}

/** Lignes du classement Live School (nom affiché = prénom + initiale). PUR. */
export function leaderboardRankingRows(rows: LeaderboardRow[], userId: string): RankingRow[] {
  return rows.map((row) => ({
    key: `${row.rank}-${row.student_id}`,
    rank: row.rank,
    name: shortName(row.display_name),
    avatarUrl: row.avatar_url,
    score: row.total_score,
    isMe: row.student_id === userId,
  }));
}

/** « Léa Martin » → « Léa M. » (classement public). PUR. */
export function shortName(displayName: string | null | undefined): string {
  const parts = (displayName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Élève';
  if (parts.length === 1) return parts[0]!;
  const initial = parts[parts.length - 1]!.charAt(0).toUpperCase();
  return `${parts[0]} ${initial}.`;
}
